import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AUTH_SERVICE, TOKEN_SERVICE } from '@core/tokens/auth.tokens';

/** Default landing for authenticated users hitting a public route. */
const AUTHENTICATED_HOME = '/admin';

/**
 * Guard para rutas protegidas.
 *
 * Verifica que el usuario esté autenticado (o tenga un token vigente) antes de
 * permitir el acceso; en caso contrario limpia los tokens y redirige al login.
 * @returns `true` si hay sesión válida; un `UrlTree` hacia `/auth/login` si no.
 */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AUTH_SERVICE);
  const tokenService = inject(TOKEN_SERVICE);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    return true;
  }

  const token = tokenService.getAccessToken();
  if (token && !tokenService.isTokenExpired(token)) {
    return true;
  }

  // Token expired or missing — clean up and redirect to login
  if (token) {
    tokenService.clearTokens();
  }

  return router.createUrlTree(['/auth/login']);
};

/**
 * Guard para rutas públicas (login).
 *
 * Redirige usuarios autenticados al home del admin; permite el acceso a usuarios
 * no autenticados (limpiando tokens expirados). basement usaba
 * NavigationService.getLastRoute(); en el chasis CMS se redirige directo a `/admin`.
 * @param _route Snapshot de la ruta activada (no utilizado; firma de `CanActivateFn`).
 * @returns `true` para usuarios no autenticados; un `UrlTree` hacia `/admin` si ya hay sesión.
 */
export const publicGuard: CanActivateFn = (_route: ActivatedRouteSnapshot) => {
  const authService = inject(AUTH_SERVICE);
  const tokenService = inject(TOKEN_SERVICE);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    return router.parseUrl(AUTHENTICATED_HOME);
  }

  const token = tokenService.getAccessToken();
  if (token && !tokenService.isTokenExpired(token)) {
    return router.parseUrl(AUTHENTICATED_HOME);
  }

  // Token expired — clean up and allow access to public route
  if (token) {
    tokenService.clearTokens();
  }

  return true;
};
